import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Button,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  listUserNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  deleteUserNotifications,
} from "../../services/notificationsServices";

const TYPE_COLOR = {
  sales: "#198754",
  moloni_documents: "#dc3545",
  reservation_expiring: "#ffc107",
  task_assigned: "#0d6efd",
  dismantle_completed: "#20c997",
};

const TYPE_LABEL = {
  sales: "Vendas",
  moloni_documents: "Documentos Moloni",
  reservation_expiring: "Reserva a expirar",
  task_assigned: "Nova tarefa",
  dismantle_completed: "Desmantelamento concluído",
};

const NotificationsHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(false);
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, by_type: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listUserNotifications({
        unreadOnly: filter === "unread",
        limit: 100,
      });
      const data = r?.data?.data || {};
      setItems(data.rows || []);
      setCounts(data.counts || { total: 0, by_type: {} });
    } catch (e) {
      toast.error("Erro ao carregar notificações.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const onClickItem = async (item) => {
    setActioning(true);
    try {
      if (!item.read_at) await markNotificationRead(item.id);
    } catch (_) {}
    setActioning(false);

    // Deep-link: usa os IDs em customData para abrir directamente o
    // recurso correcto. Fallback para a listagem quando não houver id.
    const data = item?.data || {};
    const type = data.type || item.alert_type;
    const saleId = data.id;
    const taskId = data.task_id;
    const reservationId = data.reservation_id;
    const vehicleId = data.vehicle_id;
    const documentId = data.document_id;
    switch (type) {
      case "sales":
      case "refund":
        navigate(saleId ? `/sales-details/${saleId}` : "/sales");
        break;
      case "moloni_documents":
      case "moloni_documents_info":
      case "invoice_canceled":
      case "invoice_deleted":
      case "credit_note_emitted":
      case "credit_note_partial":
      case "invoice_external_baixa":
      case "invoice_finalized_externally":
        // Se há document_id concreto, leva o user à tabela faturação
        // com search pré-preenchido para a factura específica. Senão,
        // /stock-alerts (ou /faturacao se for só info).
        if (documentId) {
          navigate(`/faturacao?search=${encodeURIComponent(documentId)}`);
        } else if (type === "moloni_documents_info") {
          navigate("/faturacao");
        } else {
          navigate("/stock-alerts");
        }
        break;
      case "reservation_expiring":
        navigate(reservationId ? `/show-reserves-list?id=${reservationId}` : "/reserves");
        break;
      case "task_assigned":
        navigate(taskId ? `/tasks?id=${taskId}` : "/tasks");
        break;
      case "dismantle_completed":
        navigate(vehicleId ? `/vehicle/details/${vehicleId}` : "/vehicle");
        break;
      default:
        await load();
    }
  };

  const markAll = async () => {
    setActioning(true);
    try {
      await markAllNotificationsRead();
      toast.success("Todas marcadas como lidas.");
      await load();
    } catch (e) {
      toast.error("Erro a marcar como lidas.");
    } finally {
      setActioning(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    setItems((arr) => arr.filter((x) => x.id !== id));
    try {
      await deleteNotification(id);
    } catch (_) {
      toast.error("Erro ao apagar.");
      load();
    }
  };

  const clearAllVisible = async () => {
    if (
      !window.confirm(
        "Apagar todas as notificações listadas? Esta acção é irreversível."
      )
    ) return;
    setActioning(true);
    try {
      const ids = items.map((n) => n.id);
      if (ids.length > 0) await deleteUserNotifications({ ids });
      toast.success("Apagadas.");
      await load();
    } catch (e) {
      toast.error("Erro ao apagar.");
    } finally {
      setActioning(false);
    }
  };

  const clearReadOnly = async () => {
    if (
      !window.confirm(
        "Apagar todas as notificações já lidas? Esta acção é irreversível."
      )
    ) return;
    setActioning(true);
    try {
      const r = await deleteUserNotifications({ read_only: 1 });
      const n = r?.data?.data?.deleted ?? 0;
      toast.success(`${n} apagada${n === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      toast.error("Erro ao apagar.");
    } finally {
      setActioning(false);
    }
  };

  const clearOlderThan = async (days) => {
    if (
      !window.confirm(
        `Apagar todas as notificações com mais de ${days} dias? Esta acção é irreversível.`
      )
    ) return;
    setActioning(true);
    try {
      const r = await deleteUserNotifications({ older_than_days: days });
      const n = r?.data?.data?.deleted ?? 0;
      toast.success(`${n} apagada${n === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      toast.error("Erro ao apagar.");
    } finally {
      setActioning(false);
    }
  };

  return (
    <div className="container-fluid p-3">
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-bell" /> Histórico de notificações
            </h5>
            <small className="text-muted">
              Recebidas em todos os dispositivos com sessão activa.
            </small>
          </div>
          <div className="d-flex gap-2">
            {counts.total > 0 && (
              <Button
                color="primary"
                size="sm"
                onClick={markAll}
                disabled={actioning}
              >
                <i className="bi bi-check2-all" /> Marcar todas como lidas
              </Button>
            )}
            <UncontrolledDropdown>
              <DropdownToggle
                color="outline-danger"
                size="sm"
                caret
                disabled={actioning}
              >
                <i className="bi bi-trash" /> Apagar
              </DropdownToggle>
              <DropdownMenu end>
                {items.length > 0 && (
                  <DropdownItem onClick={clearAllVisible}>
                    Apagar todas listadas ({items.length})
                  </DropdownItem>
                )}
                <DropdownItem onClick={clearReadOnly}>
                  Apagar todas as lidas
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem header>Por idade</DropdownItem>
                <DropdownItem onClick={() => clearOlderThan(30)}>
                  Apagar &gt; 30 dias
                </DropdownItem>
                <DropdownItem onClick={() => clearOlderThan(90)}>
                  Apagar &gt; 90 dias
                </DropdownItem>
                <DropdownItem onClick={() => clearOlderThan(180)}>
                  Apagar &gt; 6 meses
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </CardHeader>
        <CardBody>
          <div className="mb-3">
            <Button
              color={filter === "all" ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => setFilter("all")}
              className="me-1"
            >
              Todas
            </Button>
            <Button
              color={filter === "unread" ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => setFilter("unread")}
            >
              Não lidas{" "}
              {counts.total > 0 && (
                <Badge color="danger" pill className="ms-1">
                  {counts.total}
                </Badge>
              )}
            </Button>
          </div>

          {loading && <Spinner size="sm" />}

          {!loading && items.length === 0 && (
            <div
              className="text-muted text-center py-4"
              style={{ border: "1px dashed #dee2e6", borderRadius: 8 }}
            >
              Sem notificações neste filtro.
            </div>
          )}

          {items.map((n) => {
            const type = n.alert_type;
            const color = TYPE_COLOR[type] || "#6c757d";
            const label = TYPE_LABEL[type] || type;
            return (
              <div
                key={n.id}
                onClick={() => onClickItem(n)}
                style={{
                  border: "1px solid #e9ecef",
                  borderLeft: `4px solid ${color}`,
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 8,
                  cursor: "pointer",
                  background: !n.read_at ? "#f8f9fc" : "#fff",
                }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <strong style={{ fontSize: 14 }}>
                    {!n.read_at && (
                      <span
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: color,
                          marginRight: 6,
                        }}
                      />
                    )}
                    {n.title}
                  </strong>
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: 11, color: "#888" }}>
                      {timeAgo(n.sent_at)}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-muted p-0"
                      onClick={(e) => remove(n.id, e)}
                      title="Apagar"
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
                  {n.message}
                </div>
                <span
                  className="badge mt-2"
                  style={{
                    background: color,
                    color: "#fff",
                    fontSize: 10,
                  }}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </CardBody>
      </Card>
    </div>
  );
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString("pt-PT");
}

export default NotificationsHistory;
